import React, { useState } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Link from "@material-ui/core/Link";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";

const useStyles = makeStyles(theme => ({
  contenido: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    minWidth: 420
  },
  ayuda: {
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.type === "dark" ? "#263238" : "#e8f4fd",
    borderLeft: `4px solid #1976d2`,
    marginBottom: theme.spacing(1)
  },
  resultado: {
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.type === "dark" ? "#1b3a26" : "#e8f5e9",
    borderLeft: `4px solid ${green[600]}`
  },
  btnWrapper: { position: "relative" },
  progreso: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  }
}));

const esquema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Muy corto")
    .max(50, "Muy largo")
    .required("Ponle un nombre para identificar esta conexión"),
  phoneNumberId: Yup.string()
    .matches(/^[0-9]+$/, "Solo números")
    .required("Este dato es obligatorio"),
  tokenMeta: Yup.string().required("Este dato es obligatorio")
});

const WhatsAppCloudModal = ({ open, onClose }) => {
  const classes = useStyles();
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [resultado, setResultado] = useState(null);

  const valoresIniciales = {
    name: "",
    phoneNumberId: "",
    wabaId: "",
    tokenMeta: "",
    isDefault: false
  };

  const cerrar = () => {
    setResultado(null);
    setSelectedQueueIds([]);
    onClose();
  };

  const guardar = async valores => {
    try {
      const { data } = await api.post("/whatsappcloud/", {
        ...valores,
        queueIds: selectedQueueIds
      });

      setResultado({
        numero: data?.numero,
        nombreVerificado: data?.nombreVerificado,
        calidad: data?.calidad,
        suscrita: data?.suscrita
      });

      toast.success("Conexión creada y verificada con Meta");
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={cerrar} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>Conectar WhatsApp por API oficial</DialogTitle>

      <Formik
        initialValues={valoresIniciales}
        enableReinitialize
        validationSchema={esquema}
        onSubmit={(valores, actions) => {
          setTimeout(() => {
            guardar(valores);
            actions.setSubmitting(false);
          }, 200);
        }}
      >
        {({ values, touched, errors, isSubmitting }) => (
          <Form>
            <DialogContent dividers>
              <div className={classes.contenido}>
                <Paper elevation={0} className={classes.ayuda}>
                  <Typography variant="body2">
                    Los tres datos que siguen los entrega Meta en{" "}
                    <Link
                      href="https://developers.facebook.com/apps"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      developers.facebook.com
                    </Link>
                    , en tu app → <strong>WhatsApp → Configuración de la API</strong>.
                  </Typography>
                </Paper>

                <Field
                  as={TextField}
                  label="Nombre de la conexión"
                  name="name"
                  autoFocus
                  error={touched.name && Boolean(errors.name)}
                  helperText={
                    (touched.name && errors.name) ||
                    "Para identificarla en tu lista. Ejemplo: «WhatsApp Ventas»"
                  }
                  variant="outlined"
                  fullWidth
                />

                <Field
                  as={TextField}
                  label="Identificador del número de teléfono"
                  name="phoneNumberId"
                  error={touched.phoneNumberId && Boolean(errors.phoneNumberId)}
                  helperText={
                    (touched.phoneNumberId && errors.phoneNumberId) ||
                    "Número largo que aparece como «Phone number ID». No es el teléfono."
                  }
                  variant="outlined"
                  fullWidth
                />

                <Field
                  as={TextField}
                  label="Identificador de la cuenta de WhatsApp Business"
                  name="wabaId"
                  helperText="Opcional pero muy recomendable: con esto damos de alta tu cuenta con la app automáticamente, que es el paso que Meta no pide y sin el cual no llegan los mensajes."
                  variant="outlined"
                  fullWidth
                />

                <Field
                  as={TextField}
                  label="Token de acceso"
                  name="tokenMeta"
                  type="password"
                  error={touched.tokenMeta && Boolean(errors.tokenMeta)}
                  helperText={
                    (touched.tokenMeta && errors.tokenMeta) ||
                    "Usa un token permanente de usuario del sistema. El de la pantalla de pruebas vence a las 24 horas."
                  }
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={2}
                />

                <QueueSelect
                  selectedQueueIds={selectedQueueIds}
                  onChange={valoresSeleccionados =>
                    setSelectedQueueIds(valoresSeleccionados)
                  }
                />

                <FormControlLabel
                  control={
                    <Field
                      as={Switch}
                      color="primary"
                      name="isDefault"
                      checked={values.isDefault}
                    />
                  }
                  label="Usar como conexión predeterminada"
                />

                {resultado && (
                  <Paper elevation={0} className={classes.resultado}>
                    <Typography variant="body2">
                      <strong>Conectado correctamente</strong>
                    </Typography>
                    <Typography variant="body2">
                      Número: {resultado.numero || "—"}
                    </Typography>
                    <Typography variant="body2">
                      Nombre verificado: {resultado.nombreVerificado || "—"}
                    </Typography>
                    <Typography variant="body2">
                      Calidad según Meta: {resultado.calidad || "—"}
                    </Typography>
                    <Typography variant="body2">
                      {resultado.suscrita
                        ? "Cuenta dada de alta con la app: sí"
                        : "Cuenta dada de alta con la app: no se pudo. Si no llegan mensajes, es por esto."}
                    </Typography>
                  </Paper>
                )}
              </div>
            </DialogContent>

            <DialogActions>
              <Button onClick={cerrar} color="secondary" disabled={isSubmitting}>
                {resultado ? "Cerrar" : "Cancelar"}
              </Button>
              {!resultado && (
                <div className={classes.btnWrapper}>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={isSubmitting}
                  >
                    Verificar y conectar
                  </Button>
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.progreso} />
                  )}
                </div>
              )}
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default WhatsAppCloudModal;
